import { widgetApi } from "../services/api";

/**
 * Typed wrappers over the `/widget/catalog/*` endpoints exposed by
 * raas-widgets-backend. Destination countries and payout-method discovery
 * power the request compose screen; payers/states feed payout details.
 * The widget unwraps `data.items` here.
 *
 * The `x-link-id` header is injected by the shared `widgetApi` interceptor.
 */

export type {
  CountryCapability,
  DestinationOption,
  EffectiveStatus,
} from "./catalog-types";
export {
  corridorCurrencyCodesByDestination,
  currenciesForCountry,
  currencyForCountry,
  expandCountryCurrencyOptions,
  mergeCatalogWithCorridorCurrencies,
  pickDefaultDestinationCurrency,
} from "../utils/destinationOptions";
import type { CountryCapability, EffectiveStatus } from "./catalog-types";

export interface PayoutMethodCapability {
  payoutMethod: string;
  displayName: string;
  requiredFields: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

export interface PayerCapability {
  payerCode: string;
  displayName: string;
  countryCode: string;
  payoutMethods: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
  instructions?: string;
  accountPrefixes?: string[];
  constraints?: Record<string, unknown>;
}

export interface StateCapability {
  countryCode: string;
  stateCode: string;
  stateName: string;
  payoutMethods: string[];
  payerCodes?: string[];
  supportedByCorridors: string[];
  effectiveStatus: EffectiveStatus;
}

export interface CatalogCountriesInput {
  originCountryCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

interface CatalogListResponse<T> {
  success: boolean;
  data: { items: T[] };
}

const toQueryString = (params: object): string => {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export interface CatalogPayoutMethodsInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  stateCode?: string;
  cityCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

/**
 * Destination countries reachable from the origin. Powers the editable
 * destination selector on the request compose screen.
 * GET /widget/catalog/countries
 */
export const getCatalogCountries = async (
  input: CatalogCountriesInput = {}
): Promise<CountryCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<CountryCapability>>(
    `/widget/catalog/countries${toQueryString(input)}`
  );
  return data.data.items;
};

export const getCatalogPayoutMethods = async (
  input: CatalogPayoutMethodsInput
): Promise<PayoutMethodCapability[]> => {
  const { data } = await widgetApi.get<
    CatalogListResponse<PayoutMethodCapability>
  >(`/widget/catalog/payout-methods${toQueryString(input)}`);
  return data.data.items;
};

export interface CatalogPayersInput {
  originCountryCode?: string;
  destinationCountryCode: string;
  stateCode?: string;
  cityCode?: string;
  payoutMethod?: string;
  /** ISO 4217 — narrows to capabilities that list this destination currency. */
  currencyCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

export const getCatalogPayers = async (
  input: CatalogPayersInput
): Promise<PayerCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<PayerCapability>>(
    `/widget/catalog/payers${toQueryString(input)}`
  );
  return data.data.items;
};

export interface CatalogStatesInput {
  originCountryCode?: string;
  payoutMethod?: string;
  payerCode?: string;
  fundingMethod?: string;
  includeUnavailable?: boolean;
}

export const getCatalogStates = async (
  countryCode: string,
  input: CatalogStatesInput = {}
): Promise<StateCapability[]> => {
  const { data } = await widgetApi.get<CatalogListResponse<StateCapability>>(
    `/widget/catalog/countries/${countryCode}/states${toQueryString(input)}`
  );
  return data.data.items;
};
